import type { Directive } from 'vue'
import { useUiStore } from '@/stores/ui.store.ts'

interface LongPressElement extends HTMLElement {
  _longPressHandlers: {
    touchstart: (e: TouchEvent) => void
    touchmove: (e: TouchEvent) => void
    touchend: () => void
    touchcancel: () => void
  }
  _longPressTimer?: ReturnType<typeof setTimeout>
  _touchStartX?: number
  _touchStartY?: number
}

const vLongPress: Directive<LongPressElement> = {
  beforeMount(el, binding) {
    const { disableContextMenu, enableContextMenu } = useUiStore()
    const delay = Number(binding.arg) || 600
    const threshold = 15 // pixels movement allowance

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return

      el._touchStartX = e.touches[0].clientX
      el._touchStartY = e.touches[0].clientY
      disableContextMenu()
      el._longPressTimer = setTimeout(() => {
        binding.value(e)
        el._longPressTimer = undefined
        setTimeout(() => {
          enableContextMenu()
        }, 100)
      }, delay)
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (!el._longPressTimer || !el._touchStartX || !el._touchStartY) return

      const deltaX = Math.abs(e.touches[0].clientX - el._touchStartX)
      const deltaY = Math.abs(e.touches[0].clientY - el._touchStartY)

      if (deltaX > threshold || deltaY > threshold) {
        clearTimeout(el._longPressTimer)
        el._longPressTimer = undefined
      }
    }

    const cleanup = () => {
      if (el._longPressTimer) {
        clearTimeout(el._longPressTimer)
        enableContextMenu()
        el._longPressTimer = undefined
      }
    }

    // Store handlers on element for cleanup
    el._longPressHandlers = {
      touchstart: handleTouchStart,
      touchmove: handleTouchMove,
      touchend: cleanup,
      touchcancel: cleanup,
    }

    // Add event listeners
    el.addEventListener('touchstart', el._longPressHandlers.touchstart, { passive: true })
    el.addEventListener('touchmove', el._longPressHandlers.touchmove, { passive: true })
    el.addEventListener('touchend', el._longPressHandlers.touchend, { passive: true })
    el.addEventListener('touchcancel', el._longPressHandlers.touchcancel, { passive: true })
  },

  unmounted(el) {
    // Cleanup event listeners
    el.removeEventListener('touchstart', el._longPressHandlers.touchstart)
    el.removeEventListener('touchmove', el._longPressHandlers.touchmove)
    el.removeEventListener('touchend', el._longPressHandlers.touchend)
    el.removeEventListener('touchcancel', el._longPressHandlers.touchcancel)

    // Clear any remaining timer
    if (el._longPressTimer) clearTimeout(el._longPressTimer)
  },
}

export default vLongPress
